import React from 'react';
import { useMutation } from '@apollo/client';

import { Loader, Container } from '@crehana/ui';

import { useTranslation } from '@crehana/i18n';

import { customLocalStorage } from '@crehana/utils';

import { enrollmentUrlWithSourceDetail } from '../../utils';
import SEND_SUBSCRIPTION_MUTATION from '../../graphql/courseSubscribe.graphql';

import { CourseAgendaFormatted } from '../../types';

import { enrollToCourse } from '../../utils';

import HorizontalCard from './HorizontalCard';

import { CourseAgendaProps } from './types';

const CourseAgenda: React.FC<
  CourseAgendaProps & {
    showPhoneNumberDialog(courseEnrollmentUrl: string): void;
    loadingAgenda: boolean;
  }
> = ({
  errorAgenda,
  courses,
  user,
  refetchAgenda,
  setOpen,
  showPhoneNumberDialog,
  loadingAgenda,
}) => {
  const { t } = useTranslation();
  const [sendSubscription] = useMutation(SEND_SUBSCRIPTION_MUTATION, {
    onCompleted({ courseSubscribe }) {
      if (courseSubscribe && courseSubscribe.success) {
        customLocalStorage.removeItem('courseId');
        refetchAgenda();
        setOpen(true);
      }
    },
  });

  React.useEffect(() => {
    window.requestAnimationFrame(() => {
      const { hash } = window.location;

      if (hash === '#agenda') {
        const sectionNode = document.querySelector('#course-agenda-section');

        if (sectionNode && sectionNode.scrollIntoView) {
          sectionNode.scrollIntoView({ behavior: 'smooth' });
        }
      }
    });
  }, []);

  const onClickCTA = ({
    originalId,
    enrollmentUrl,
    courseType,
  }: CourseAgendaFormatted) => {
    customLocalStorage.setItem('courseId', originalId.toString());
    if (!user) {
      window.location.href = `/onboarding/?nextUrl=${window.location.href}`;
      // when the courseType is temporal acccess it means the course is free for everyone!
      // So We just need to redirect the user to the enrollmentUrl
      // if (courseType === 'temporal-access') {
    } else if (courseType === 'temporal-access') {
      if (user.phoneNumber && user.phoneNumber.length > 0) {
        enrollToCourse(enrollmentUrl);
      } else {
        showPhoneNumberDialog(enrollmentUrl);
      }
    } else if (user.phoneNumber && user.phoneNumber.length > 0) {
      sendSubscription({
        variables: {
          courseId: originalId,
          contentType: 'crehana-temporal-access',
        },
      });
    } else {
      customLocalStorage.setItem('courseId', originalId.toString());
      showPhoneNumberDialog(window.location.href);
    }
  };

  React.useEffect(() => {
    const courseIdToEnroll = customLocalStorage.getItem('courseId');

    if (courseIdToEnroll && user) {
      const courseId = parseInt(courseIdToEnroll, 10);

      sendSubscription({
        variables: { courseId, contentType: 'crehana-temporal-access' },
      });
    }
  }, [user]);

  if (loadingAgenda)
    return (
      <div className="flex items-center justify-center my-32 md:my-48">
        <Loader />
      </div>
    );

  if (errorAgenda) {
    return (
      <div className="flex justify-center items-center">
        <p className="text-purple-main font-body2 my-32 md:my-48">
          {t('COURSE_AGENDA_ERROR')}
        </p>
      </div>
    );
  }

  return (
    <div
      /* We need the id to scroll to this section */
      /**
       * You can scroll to this section by:
       * 1. Clicking on the "Ver más cursos" button in the CourseFree component.
       * 2. Adding a hash at the end of the url: /curso-gratis-del-dia/#agenda
       */
      id="course-agenda-section"
      className="mt-32 sm:mt-48 md:mt-48 mb-80"
    >
      <Container className="relative flex flex-start items-start flex-wrap">
        <a
          href="#agenda"
          className="font-h3 text-base-main mb-8 sm:mb-32 flex justify-center text-center w-full"
        >
          {t('COURSE_AGENDA_TITLE')}
        </a>
        {courses.map(course => {
          const enrollmentUrl = enrollmentUrlWithSourceDetail.getUrl({
            originalId: course.originalId,
            enrollmentUrl: course.enrollmentUrl,
            sourceDetail: enrollmentUrlWithSourceDetail.SOURCES.COURSE_AGENDA,
          });

          return (
            <HorizontalCard
              course={course}
              key={course.originalId}
              onClickCTA={() => {
                onClickCTA({ ...course, enrollmentUrl });
              }}
              enrollToCourse={() => {
                enrollToCourse(enrollmentUrl);
              }}
            />
          );
        })}
      </Container>
    </div>
  );
};

export default CourseAgenda;
